import type { HallPatrolTankSnapshot } from "./hallPatrol";

const DB_NAME = "discus-ai-v2-hall-patrol";
const DB_VERSION = 1;
const STORE_NAME = "tank_snapshots";

interface CachedTankSnapshotSet {
  farmId: string;
  version: 1;
  cachedAt: string;
  tanks: HallPatrolTankSnapshot[];
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("QR_CACHE_REQUEST_FAILED"));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () =>
      reject(transaction.error ?? new Error("QR_CACHE_TRANSACTION_FAILED"));
    transaction.onabort = () =>
      reject(transaction.error ?? new Error("QR_CACHE_TRANSACTION_ABORTED"));
  });
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME, { keyPath: "farmId" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("QR_CACHE_OPEN_FAILED"));
    request.onblocked = () => reject(new Error("QR_CACHE_OPEN_BLOCKED"));
  });
}

export async function readHallPatrolTankCache(
  farmId: string,
): Promise<HallPatrolTankSnapshot[] | undefined> {
  const database = await openDatabase();
  try {
    const transaction = database.transaction(STORE_NAME, "readonly");
    const record = await requestResult(
      transaction.objectStore(STORE_NAME).get(farmId) as IDBRequest<
        CachedTankSnapshotSet | undefined
      >,
    );
    await transactionDone(transaction);
    return record?.version === 1 ? structuredClone(record.tanks) : undefined;
  } finally {
    database.close();
  }
}

export async function writeHallPatrolTankCache(
  farmId: string,
  tanks: readonly HallPatrolTankSnapshot[],
): Promise<void> {
  const database = await openDatabase();
  try {
    const transaction = database.transaction(STORE_NAME, "readwrite");
    transaction.objectStore(STORE_NAME).put({
      farmId,
      version: 1,
      cachedAt: new Date().toISOString(),
      tanks: structuredClone([...tanks]),
    } satisfies CachedTankSnapshotSet);
    await transactionDone(transaction);
  } finally {
    database.close();
  }
}

export async function clearHallPatrolTankCache(farmId: string): Promise<void> {
  const database = await openDatabase();
  try {
    const transaction = database.transaction(STORE_NAME, "readwrite");
    transaction.objectStore(STORE_NAME).delete(farmId);
    await transactionDone(transaction);
  } finally {
    database.close();
  }
}
